import React, { useEffect, useRef, useState } from 'react';
import { GameLoader } from '../services/GameLoader';
import { GameKey } from '../types/game';

interface ShadowDOMGameProps {
  gameKey: GameKey;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

export const ShadowDOMGame: React.FC<ShadowDOMGameProps> = ({
  gameKey,
  onLoad,
  onError
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadGame = async () => {
      if (!containerRef.current) return;

      try {
        setLoading(true);
        setError(null);

        const gameLoader = new GameLoader();
        const gameContainer = await gameLoader.loadGame(gameKey);

        // Очищаем предыдущее содержимое
        while (containerRef.current.firstChild) {
          containerRef.current.removeChild(containerRef.current.firstChild);
        }

        // Добавляем новую игру
        containerRef.current.appendChild(gameContainer);
        
        setLoading(false);
        onLoad?.();
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
        onError?.(err as Error);
        setLoading(false);
      }
    };

    loadGame();

    // Очистка при размонтировании
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [gameKey, onLoad, onError]);

  return (
    <div className="shadow-dom-game-container" style={{ width: '100%', height: '100%' }}>
      {loading && (
        <>
          <style>{`
            @keyframes shadow-dom-spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .shadow-dom-loading-spinner {
              width: 50px;
              height: 50px;
              border: 4px solid rgba(0, 0, 0, 0.1);
              border-top: 4px solid #333;
              border-radius: 50%;
              animation: shadow-dom-spin 1s linear infinite;
              margin-bottom: 20px;
            }
          `}</style>
          <div className="game-loading" style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div className="shadow-dom-loading-spinner" />
            <div style={{ fontSize: '18px' }}>Loading game...</div>
          </div>
        </>
      )}
      {error && (
        <div className="game-error" style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'red',
          fontSize: '18px'
        }}>
          Error: {error}
        </div>
      )}
      <div 
        ref={containerRef} 
        style={{ 
          width: '100%', 
          height: '100%',
          display: loading || error ? 'none' : 'block'
        }} 
      />
    </div>
  );
};
